import {
  Avatar,
  Box,
  createStyles,
  Group,
  MantineNumberSize,
  Text,
  Tooltip,
  useMantineTheme
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import React from 'react'

interface Props {
  title: React.ReactNode | string
  picture?: string | null
  pictureRadius?: MantineNumberSize
  subtitle?: React.ReactNode | string
  subtitlePosition?: 'before' | 'after'
  subtitleHiddenXs?: boolean
  disablePicture?: boolean
  disableTooltip?: boolean
  fullWidth?: boolean
  isReverted?: boolean
  size?: 'small' | 'medium'
}

const useStyles = createStyles(theme => ({
  avatar: { border: `1px solid ${theme.colors.gray[3]}`, borderRadius: '50%' },
  title: {
    lineHeight: 1,
    paddingBottom: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  subtitle: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  titleTooltip: { backgroundColor: theme.colors[theme.primaryColor][7] }
}))

export default function PictureCard({
  title,
  picture,
  pictureRadius = 'xl',
  subtitle,
  subtitlePosition = 'after',
  subtitleHiddenXs = true,
  disablePicture = false,
  disableTooltip = false,
  fullWidth = false,
  isReverted = false,
  size = 'medium'
}: Props) {
  // Hooks
  const theme = useMantineTheme()
  const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`)
  const { classes } = useStyles()

  const subtitleText = () => (
    <Text
      size="sm"
      color="dimmed"
      align={isReverted ? 'right' : 'left'}
      hidden={isXs && subtitleHiddenXs}
      className={classes.subtitle}>
      {subtitle}
    </Text>
  )

  return (
    <Group
      position={isReverted ? 'right' : 'left'}
      spacing="sm"
      noWrap
      w={fullWidth ? '100%' : 'auto'}>
      {!isReverted && picture !== undefined && (
        <Avatar
          src={picture}
          radius={pictureRadius}
          size={isXs || size === 'small' ? 30 : 40}
          hidden={disablePicture}
          className={classes.avatar}
        />
      )}
      <Box sx={{ overflow: 'hidden' }} w={fullWidth ? '100%' : 'auto'}>
        <>
          {subtitlePosition === 'before' && subtitleText()}
          <Tooltip
            label={title}
            position="top-end"
            withArrow
            disabled={disableTooltip}
            classNames={{ tooltip: classes.titleTooltip }}>
            <Text
              size={isXs || size === 'small' ? 'sm' : 'md'}
              align={isReverted ? 'right' : 'left'}
              className={classes.title}>
              {title}
            </Text>
          </Tooltip>
          {subtitlePosition === 'after' && subtitleText()}
        </>
      </Box>
      {isReverted && picture !== undefined && (
        <Avatar
          src={picture}
          radius={pictureRadius}
          size={isXs || size === 'small' ? 30 : 40}
          hidden={disablePicture}
        />
      )}
    </Group>
  )
}
